import { BasicImageProps } from '../Image/Image'
import { OffersFilterProps, TagProps } from './OffersFilter'
import { getCardsToShow, filterCardsByTag } from './OffersFilter.helpers'

// An enum with all the types of actions to use in our reducer
export enum ActionType {
  TAG_FILTER_CHANGED = 'TAG_FILTER_CHANGED',
  SEARCH_FILTER_CHANGED = 'SEARCH_FILTER_CHANGED',
  SEARCH_INPUT_CHANGED = 'SEARCH_INPUT_CHANGED',
  CLEAR_SEARCH = 'CLEAR_SEARCH',
  SHOW_MORE = 'SHOW_MORE',
  SYNC_URL_PARAMS = 'SYNC_URL_PARAMS',
  UPDATE_MOBILE_STATE = 'UPDATE_MOBILE_STATE',
}

export const SHOW_BY = 9
// Number of cards to show per page on mobile devices
export const SHOW_BY_MOBILE = 3
export const SHOW_ALL_PRODUCTS = 'ALL'

type Action = {
  type: ActionType
  payload?: string | string[] | { tags: string[] } | boolean
}

type State = {
  allCards?: OffersFilterProps['offerCards']
  filteredCards?: OffersFilterProps['offerCards']
  activeTags?: string[]
  searchTerm?: string
  searchInputValue?: string
  isFirstSearch?: boolean
  colsToShow?: number
  cardsToShow?: number
  showMoreEnabled?: boolean
  showLoadMoreBtn?: boolean
  urlTagValue?: string[]
  filterTags: OffersFilterProps['filterTags']
  SSRSafeMobile?: boolean
}

export const OfferFilterReducer: React.Reducer<State, Action> = (state, action) => {
  const { type, payload } = action

  switch (type) {
    case ActionType.TAG_FILTER_CHANGED:
      let activeTags = payload as string[]
      let newSearchTerm = state.searchTerm

      // Remove All tag if other tag selected
      if (activeTags.length > 1 && activeTags.at(0) === SHOW_ALL_PRODUCTS) activeTags = activeTags.slice(1)

      // If All tag selected unselect all other tags and clear search
      if (activeTags.length === 0 || activeTags.at(-1) === SHOW_ALL_PRODUCTS) {
        activeTags = [SHOW_ALL_PRODUCTS]
        newSearchTerm = '' // Clear search when "All" is selected
      }

      // Apply filtering after tag logic is handled
      const filteredCards = applyAllFilters(state.allCards, activeTags, newSearchTerm)

      return {
        ...state,
        filteredCards,
        activeTags,
        searchTerm: newSearchTerm,
        showMoreEnabled: hasMoreCards(filteredCards, state.cardsToShow),
      }

    case ActionType.SEARCH_INPUT_CHANGED:
      return {
        ...state,
        searchInputValue: payload as string,
      }

    case ActionType.SEARCH_FILTER_CHANGED:
      const searchTerm = payload as string
      const searchFilteredCards = applyAllFilters(state.allCards, state.activeTags, searchTerm)

      return {
        ...state,
        searchTerm,
        searchInputValue: searchTerm,
        isFirstSearch: false,
        filteredCards: searchFilteredCards,
        showMoreEnabled: hasMoreCards(searchFilteredCards, state.cardsToShow),
      }

    case ActionType.CLEAR_SEARCH:
      const clearedSearchCards = applyAllFilters(state.allCards, state.activeTags, '')

      return {
        ...state,
        searchTerm: '',
        searchInputValue: '',
        filteredCards: clearedSearchCards,
        showMoreEnabled: hasMoreCards(clearedSearchCards, state.cardsToShow),
      }

    case ActionType.SHOW_MORE:
      const increment = state.SSRSafeMobile ? SHOW_BY_MOBILE : SHOW_BY // 3 or 9
      const colsToShow = (state.colsToShow ?? 0) + increment
      const cardsToShow = getCardsToShow(state.filteredCards, colsToShow)

      return {
        ...state,
        cardsToShow,
        colsToShow,
        showMoreEnabled: hasMoreCards(state.filteredCards, cardsToShow),
      }

    case ActionType.SYNC_URL_PARAMS: {
      const { tags } = payload as { tags: string[] }

      // Determine active tags from URL
      const urlTags: { id: string }[] = tags.includes(SHOW_ALL_PRODUCTS)
        ? [{ id: SHOW_ALL_PRODUCTS }]
        : state.filterTags.filter((tag) => tags.includes(tag.urlTag))

      const syncActiveTags: string[] = urlTags.length > 0 ? urlTags.map((tag) => tag.id) : [SHOW_ALL_PRODUCTS]

      // Filter cards based on URL tags
      const syncFilteredCards = urlTags.some((tag) => tag.id === SHOW_ALL_PRODUCTS)
        ? state.allCards
        : filterCardsByTag(state.allCards, urlTags)

      // Reset cols to show
      const resetColsToShow = state.SSRSafeMobile ? SHOW_BY_MOBILE : SHOW_BY
      const resetCardsToShow = getCardsToShow(syncFilteredCards, resetColsToShow)

      return {
        ...state,
        activeTags: syncActiveTags,
        filteredCards: syncFilteredCards,
        searchInputValue: '',
        searchTerm: '',
        colsToShow: state.showLoadMoreBtn ? resetColsToShow : -1,
        cardsToShow: state.showLoadMoreBtn ? resetCardsToShow : state.allCards.length,
        showMoreEnabled: state.showLoadMoreBtn && syncFilteredCards.length > resetColsToShow,
      }
    }

    case ActionType.UPDATE_MOBILE_STATE: {
      const isMobile = payload as boolean

      // Recalculate pagination based on new mobile state
      const newColsToShow = isMobile ? SHOW_BY_MOBILE : SHOW_BY
      const newCardsToShow = getCardsToShow(state.filteredCards, newColsToShow)

      return {
        ...state,
        SSRSafeMobile: isMobile,
        colsToShow: state.showLoadMoreBtn ? newColsToShow : -1,
        cardsToShow: state.showLoadMoreBtn ? newCardsToShow : state.allCards.length,
        showMoreEnabled: state.showLoadMoreBtn && (state.filteredCards?.length ?? 0) > newColsToShow,
      }
    }

    /* istanbul ignore next */
    default:
      throw Error('unknown action')
  }
}

type initialStateProps = {
  allCards: OffersFilterProps['offerCards']
  filteredCards?: OffersFilterProps['offerCards']
  activeTags?: string[]
  colsToShow?: number
  cardsToShow?: number
  showMoreEnabled?: boolean
  showLoadMoreBtn?: boolean
  urlTagValue: string[]
  filterTags: OffersFilterProps['filterTags']
  cardTags?: string[]
  SSRSafeMobile?: boolean
}

export type CardElement = {
  achorID?: string
  background?: string
  campaignBadge?: string
  cardTag?: string
  content?: React.ReactNode
  cta?: React.ReactNode
  ctaSecondary?: React.ReactNode | boolean
  fullWidth?: boolean
  heading?: React.ReactNode
  id?: string
  img?: BasicImageProps
  ribbon?: string
  tags?: TagProps[]
}

// Type definitions for content structure
interface ContentfulBlock {
  fields?: {
    text?: string
    richText?: {
      content?: Array<{
        content?: Array<{
          value?: string
        }>
      }>
    }
  }
}

interface ReactElementWithBlock {
  props?: {
    block?: ContentfulBlock
  }
}

// Type guards
function isReactElementWithBlock(value: unknown): value is ReactElementWithBlock {
  if (value === null || typeof value !== 'object' || !('props' in value)) {
    return false
  }
  const element = value as Record<string, unknown>
  return typeof element.props === 'object' && element.props !== null
}

function hasTextField(block: unknown): block is { fields: { text: string } } {
  if (block === null || typeof block !== 'object' || !('fields' in block)) {
    return false
  }
  const blockWithFields = block as Record<string, unknown>
  const fields = blockWithFields.fields
  if (fields === null || typeof fields !== 'object' || !('text' in fields)) {
    return false
  }
  const fieldsObj = fields as Record<string, unknown>
  return typeof fieldsObj.text === 'string'
}

function hasRichTextField(
  block: unknown
): block is { fields: { richText: NonNullable<ContentfulBlock['fields']>['richText'] } } {
  if (block === null || typeof block !== 'object' || !('fields' in block)) {
    return false
  }
  const blockWithFields = block as Record<string, unknown>
  const fields = blockWithFields.fields
  return typeof fields === 'object' && fields !== null && 'richText' in fields
}

export const initialState = ({
  allCards = [],
  showLoadMoreBtn,
  urlTagValue = [],
  filterTags = [],
  cardTags,
  SSRSafeMobile,
}: initialStateProps): State => {
  const cardsWithTags: OffersFilterProps['offerCards'] = allCards.filter(
    (card: CardElement) => card.cardTag && (cardTags || []).includes(card.cardTag)
  )
  const activeTagsWithSelectedDevice: string[] = cardsWithTags.flatMap((card: CardElement) =>
    (card.tags || []).map((tag: TagProps) => tag.id)
  )

  const colsToShow = SSRSafeMobile ? 3 : 9

  const urlTags: { id: string }[] = urlTagValue.includes(SHOW_ALL_PRODUCTS)
    ? [{ id: SHOW_ALL_PRODUCTS }]
    : filterTags.filter((tag) => urlTagValue.includes(tag.urlTag))

  const filteredCards = urlTags.some((tag) => tag.id === SHOW_ALL_PRODUCTS)
    ? allCards
    : filterCardsByTag(allCards, urlTags)
  const activeTags: string[] = urlTags.length > 0 ? urlTags.map((tag: { id: string }) => tag.id) : [SHOW_ALL_PRODUCTS]

  return {
    allCards,
    filteredCards: (cardTags || []).length > 0 ? cardsWithTags : filteredCards,
    activeTags: (cardTags || []).length > 0 ? activeTagsWithSelectedDevice : activeTags,
    searchTerm: '',
    searchInputValue: '',
    isFirstSearch: true,
    colsToShow: !showLoadMoreBtn ? -1 : colsToShow,
    cardsToShow: !showLoadMoreBtn ? allCards.length : getCardsToShow(allCards, colsToShow),
    showMoreEnabled: showLoadMoreBtn && allCards.length > colsToShow,
    showLoadMoreBtn,
    filterTags,
    SSRSafeMobile,
  }
}

const hasMoreCards = (cards: State['filteredCards'], cardsToShow: State['cardsToShow']) =>
  (cards?.length ?? 0) > (cardsToShow ?? 0)

export const filterOffers = (offers: State['filteredCards'], tags: State['activeTags']) =>
  offers?.filter((c) => Array.isArray(c.tags) && tags?.some((tagId) => c.tags.some((tag) => tag.id === tagId))) ?? []

/**
 * Safely extracts text content from various content structures
 * @param content - The content to extract text from (can be string or React element with Contentful block)
 * @returns The extracted text or empty string if not found
 */
const getTextContent = (content: unknown): string => {
  // Handle string content directly
  if (typeof content === 'string') {
    return content
  }

  // Handle React element with Contentful block structure
  if (isReactElementWithBlock(content)) {
    const block = content.props?.block

    // Check if block has a text field
    if (block && hasTextField(block)) {
      return block.fields.text
    }
  }

  return ''
}

/**
 * Safely extracts rich text content from Contentful block structure
 * @param content - The content to extract from
 * @returns The extracted text or empty string if not found
 */
const getRichTextContent = (content: unknown): string => {
  // Handle string content directly
  if (typeof content === 'string') {
    return content
  }

  // Handle React element with Contentful rich text block structure
  if (!isReactElementWithBlock(content)) {
    return ''
  }

  const block = content.props?.block

  if (!block || !hasRichTextField(block)) {
    return ''
  }

  const richTextContent = block.fields.richText?.content?.[0]?.content

  if (!Array.isArray(richTextContent)) {
    return ''
  }

  // Extract all 'value' properties from the array of content objects
  const textValues = richTextContent
    .map((contentObj) => {
      if (contentObj && typeof contentObj === 'object' && 'value' in contentObj) {
        return typeof contentObj.value === 'string' ? contentObj.value : ''
      }
      return ''
    })
    .filter(Boolean)

  return textValues.join(' ')
}

/**
 * Filters cards based on search term across multiple fields
 * @param cards - Array of offer cards to filter
 * @param searchTerm - Search term to filter by
 * @returns Filtered array of cards matching the search term
 */
export const filterCardsBySearch = (cards: OffersFilterProps['offerCards'], searchTerm: string) => {
  if (!searchTerm?.trim()) return cards

  const normalizedSearchTerm = searchTerm.toLowerCase()

  return cards.filter((card) => {
    // Extract text from various card fields using type-safe methods
    const headingText = getTextContent(card.heading)
    const ctaText = getTextContent(card.cta)
    const ctaTextSecondary = getTextContent(card.ctaSecondary)
    const contentText = getRichTextContent(card.content)

    // Remove legal text placeholders from content
    const cleanedContentText = contentText.replace(/\$\{[^}]*\}/g, '')

    // Search in tags
    const tagMatches =
      Array.isArray(card.tags) && card.tags.some((tag) => tag.label.toLowerCase().includes(normalizedSearchTerm))

    // Check if search term matches any field
    return (
      headingText.toLowerCase().includes(normalizedSearchTerm) ||
      cleanedContentText.toLowerCase().includes(normalizedSearchTerm) ||
      ctaText.toLowerCase().includes(normalizedSearchTerm) ||
      ctaTextSecondary.toLowerCase().includes(normalizedSearchTerm) ||
      tagMatches
    )
  })
}

const applyAllFilters = (allCards: OffersFilterProps['offerCards'], activeTags: string[], searchTerm?: string) => {
  let filtered = allCards

  // First apply tag filtering
  if (!activeTags?.includes(SHOW_ALL_PRODUCTS)) {
    const urlTagIds = new Set(activeTags)
    const tagFiltered = allCards.filter(
      (card) => Array.isArray(card.tags) && card.tags.some((tag) => urlTagIds.has(tag.id))
    )

    if (activeTags.length > 0 && tagFiltered.length === 0) {
      filtered = []
    } else {
      filtered = tagFiltered
    }
  }

  // Then apply search filtering
  if (searchTerm?.trim()) {
    filtered = filterCardsBySearch(filtered, searchTerm)
  }

  return filtered
}
